import { Component, Input, inject, ChangeDetectionStrategy } from "@angular/core";
import { Group, GroupDefinition } from "common/associations/association-group.service";
import { Container, Groups } from "common/associations/association-list.component";
import { ProcessElementsResource } from "core/stages-client";
import { ViewService } from "core/view.service";
import { Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { ComplianceProcessAssociationStore } from "process/compliance/associations/compliance-process-association-store.logic";
import { AssociationService } from "process/associations/association.service";
import { ProcessComplianceAdapter } from "process/compliance/associations/process-compliance-association-store.logic";
import { AsyncPipe } from "@angular/common";
import { AssociationListComponent } from "common/associations/association-list.component";

type ViewableElement = stages.process.ViewableElement;
type ViewableComplianceAssociationGroup = stages.process.ViewableComplianceAssociationGroup;

@Component({
	selector: "stages-compliance-process-association-list",
	templateUrl: "./compliance-process-association-list.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AssociationListComponent, AsyncPipe],
})
export class ComplianceProcessAssociationListComponent {
	private viewService = inject(ViewService);

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	classes: string = "";

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	messageKeyNone?: string;

	container$: Observable<Container<ProcessComplianceAdapter, ViewableElement>>;

	associationStore: ComplianceProcessAssociationStore;

	constructor() {
		const viewService = this.viewService;
		const associationService = inject(AssociationService);
		const processElementsResource = inject(ProcessElementsResource);

		this.associationStore = new ComplianceProcessAssociationStore(
			viewService,
			associationService,
			processElementsResource,
		);
		this.container$ = this.viewService.awaitSelfElementObservable().pipe(
			//while same id
			distinctUntilChanged((p: stages.process.ProcessView, q: stages.process.ProcessView) => p.id !== q.id),
			map((p) => {
				return {
					sourceElement: new ProcessComplianceAdapter(p),
					associationGroups: this.buildGroups(p as unknown as ViewableElement),
					preferencesKey: "complianceProcessAssociation.list.state." + p.processType + "." + p.type.ident,
				};
			}),
		);
	}

	buildGroups(self: ViewableElement): Groups<ViewableElement> {
		const result: Groups<ViewableElement> = {};
		const complianceContainer = self.complianceProcessContainer;
		if (complianceContainer) {
			for (const complianceAssociationGroup of complianceContainer) {
				result[complianceAssociationGroup.groupKey] = this.buildGroup(complianceAssociationGroup);
			}
		}
		return result;
	}

	buildGroup(associationGroup: ViewableComplianceAssociationGroup): Group<ViewableElement> {
		return {
			id: associationGroup.id,
			path: associationGroup.groupKey,
			name: associationGroup.name,
			list: associationGroup.list,
			derived: false,
			commentSupported: true,
			commentOnlyAssociationsSupported: false,
			allowAssociateDependentElements: false,
			targetType: undefined,
			targetSubtypes: undefined,
			type: associationGroup.type,
			actions: associationGroup.allowedOperations,
			workspace: associationGroup.referenceModelWorkspaceId,
			subgroups: [],
		};
	}

	asArray(groups: Groups<ViewableElement>): GroupDefinition[] {
		return Object.keys(groups).map((key) => {
			return {
				path: key,
				id: groups[key].id,
				name: groups[key].name,
				allowAssociateDependentElements: groups[key].allowAssociateDependentElements,
			};
		});
	}
}
